import { SELECTED_COMMERCIAL_PRODUCT_INDEX } from '../../common/constants/commonConstants.js';
import { getTotalSums } from '../../common/utils/commercialProductUtils.js';
import { createRowForDescription, getAddonsText, getContactOrPurposeOfUse } from '../../common/utils/supportCaseUtils';
import { SalesType } from '../../generated/api/models.js';
import type { FixedBroadbandInstallationDetails } from './FixedBroadbandSetupDate.js';
import type { AddedAddon } from '../../common/types/addon.js';
import type { ConfiguredOffer } from '../../common/types/commercialProduct.js';
import type { PurposeOfUseOrContact, SimCardConfiguration } from '../../common/types/subscription.js';
import type { CommercialProduct, CompanyInfoResponse, Contact, Offer } from '../../generated/api/models.js';
import type { AddressSearchMatch } from '../AddressSearch/AddressSearch.js';

interface YritystuplaProperties {
  offerName: string;
  purposeOfUseOrContact: PurposeOfUseOrContact;
}
export interface FixedBroadbandOrder {
  broadbandInstallationDetails?: FixedBroadbandInstallationDetails;
  commercialProductName: string;
  companyInfo: {
    postalAddress?: string;
    postCode?: string;
    postOffice?: string;
    name?: string;
    businessId?: string;
  };
  purposeOfUseOrContact: PurposeOfUseOrContact;
  installationAddress: {
    streetAddress: string;
    postalCode: string;
  };
  yritystupla?: YritystuplaProperties;
  monthlyRecurringCharge: number;
  offerName: string;
  offerTechnology?: string;
  oneTimeCharge: number;
  campaignName: string | undefined;
  simCardConfiguration: SimCardConfiguration | undefined;
  addedAddOns: AddedAddon[];
  billingAccountDisplayId?: string;
}

const TAAPI_TECHNOLOGY = 'taapiTechnology';

const getExistingSubscriptionInformation = (fixedBroadbandOrder?: FixedBroadbandOrder) => {
  return `Kyllä
     Liittymän operaattori: ${
       fixedBroadbandOrder?.broadbandInstallationDetails?.currentOperator ||
       fixedBroadbandOrder?.broadbandInstallationDetails?.existingBroadbandOperator
     }
     Liittymän sopimustunniste: ${fixedBroadbandOrder?.broadbandInstallationDetails?.existingSubscriptionContractNumber}
      `;
};

const getYritystuplaInformation = ({ yritystupla }: FixedBroadbandOrder) => {
  if (yritystupla) {
    const { purposeOfUseOrContact } = yritystupla;

    return `
      ${yritystupla.offerName}
      ${createRowForDescription('Yritystupla liittymän käyttötarkoitus', purposeOfUseOrContact.purposeOfUse)}
      ${createRowForDescription('Yritystupla liittymän kustannuspaikka', purposeOfUseOrContact.costCenter)}
      ${createRowForDescription('Yritystupla liittymän viite', purposeOfUseOrContact.employeeNumber)}
      Huom!. Muista täyttää Yritystuplan tilauksen yhteydessä käyttötarkoitus kenttään ensin Netti Lite liittymän SUB numero, ja sen perään haluttu tarkenne. Kts, Yritystuplan tilausohje Knowledgesta.
      `;
  }
  return '';
};

const getAdditionalServices = (order: FixedBroadbandOrder) => {
  const { addedAddOns, yritystupla } = order;
  if (addedAddOns.length === 0 && !yritystupla) {
    return '';
  }

  return getAddonsText(addedAddOns);
};

export const getSupportCaseDescription = (fixedBroadband: FixedBroadbandOrder): string => {
  return `
      Valitut tuotteet & saatavuustiedot:
      Valittu saatavilla oleva tuote: ${fixedBroadband?.offerName}${
        fixedBroadband?.offerTechnology ? ` (${fixedBroadband?.offerTechnology})` : ''
      }

      Asennusosoite: ${fixedBroadband?.installationAddress.streetAddress}
      Asennusosoitteen postinumero: ${fixedBroadband?.installationAddress.postalCode}
      Toivottu asennuspäivämäärä: ${fixedBroadband?.broadbandInstallationDetails?.installationDate || ''}
      ${fixedBroadband?.campaignName ? `Kampanja: ${fixedBroadband?.campaignName}` : ''}

      ${getContactOrPurposeOfUse(fixedBroadband.purposeOfUseOrContact)}

      ${createRowForDescription('Netti Lite liittymän kustannuspaikka', fixedBroadband.purposeOfUseOrContact.costCenter)}
      ${createRowForDescription('Netti Lite liittymän viite', fixedBroadband.purposeOfUseOrContact.employeeNumber)}

      Olemassa olevan laajakaistaliittymän tiedot:
      Onko kohteessa olemassa olevaa liittymää?: ${
        fixedBroadband?.broadbandInstallationDetails?.existingBroadband === 'yes'
          ? getExistingSubscriptionInformation(fixedBroadband)
          : 'Ei'
      }

      ${getAdditionalServices(fixedBroadband)}
      ${getYritystuplaInformation(fixedBroadband)}
      Laskutussopimuksen numero: ${fixedBroadband.billingAccountDisplayId}
      `
    .replace(/  +/g, '')
    .replace(/\n{3,}/g, '\n\n');
};

interface FixedBroadBandSupportCaseProps {
  contacts: Contact[];
  purposeOfUseOrContact: PurposeOfUseOrContact;
  yritystuplaPurposeOfUse?: PurposeOfUseOrContact;
  companyInfo: CompanyInfoResponse;
  broadbandInstallationDetails?: FixedBroadbandInstallationDetails;
  billingAccountDisplayId?: string;
  selectedCommercialProduct: CommercialProduct;
  addressSearchMatch: AddressSearchMatch;
  currentUrl: string;
  configuredOffer: ConfiguredOffer;
}

const getOfferTechnology = (offer: Offer) => {
  return offer.properties?.find(prop => prop.name === TAAPI_TECHNOLOGY)?.value;
};

export const createFixedBroadbandOrder = ({
  contacts,
  purposeOfUseOrContact,
  yritystuplaPurposeOfUse,
  companyInfo,
  broadbandInstallationDetails,
  billingAccountDisplayId,
  selectedCommercialProduct,
  addressSearchMatch,
  configuredOffer,
}: FixedBroadBandSupportCaseProps) => {
  const selectedContact = {
    ...purposeOfUseOrContact,
    ...contacts.find((contact: Contact) => contact.contactId === purposeOfUseOrContact.contactId),
  };

  const populatedPurposeOfUseOrContact: PurposeOfUseOrContact = {
    ...purposeOfUseOrContact,
    ...selectedContact.person,
    ...selectedContact.userContact,
  };

  const { businessId = '', companyName = '' } = companyInfo || {};
  const price = getTotalSums(configuredOffer.selectedCommercialProducts, undefined, SalesType.NEW_SALE);

  const configuredCommercialProduct = configuredOffer.selectedCommercialProducts[SELECTED_COMMERCIAL_PRODUCT_INDEX];

  const yritystuplaOffer = configuredOffer.selectedCommercialProducts.find(product =>
    product.commercialProduct.commercialProductName.match(/yritystupla/i)
  );

  const fixedBroadBandOrder: FixedBroadbandOrder = {
    companyInfo: {
      postalAddress: companyInfo.address?.line1,
      postCode: companyInfo.address?.postalCode,
      postOffice: companyInfo.address?.postOffice,
      name: companyName,
      businessId,
    },
    purposeOfUseOrContact: populatedPurposeOfUseOrContact,
    commercialProductName: selectedCommercialProduct.commercialProductName,
    broadbandInstallationDetails,
    installationAddress: {
      streetAddress: addressSearchMatch.addressText,
      postalCode: addressSearchMatch.postalCode,
    },
    yritystupla:
      yritystuplaOffer && yritystuplaPurposeOfUse
        ? {
            offerName: yritystuplaOffer.commercialProduct.commercialProductName,
            purposeOfUseOrContact: yritystuplaPurposeOfUse,
          }
        : undefined,
    monthlyRecurringCharge: price.totalMonthlyRecurringCharge,
    offerName: configuredOffer.offer.offerName,
    offerTechnology: getOfferTechnology(configuredOffer.offer),
    oneTimeCharge: price.totalOneTimeCharge,
    campaignName: configuredCommercialProduct.campaignName,
    simCardConfiguration: configuredCommercialProduct.simCardConfiguration,
    addedAddOns: configuredCommercialProduct.addedAddOns || [],
    billingAccountDisplayId,
  };

  return fixedBroadBandOrder;
};
